// PartnershipRatesFields (legacy export name CommissionRatesFields) — the reusable
// "set the new downline's PARTNERSHIP % at creation" block, used by every agent-create
// modal (admin promote + agent sub-agent).
//
// BPG-4 (2026-08-05): per-API-game partnership. The broker sets, in DEAD-SIMPLE cards:
//   🎰 Casino  = one % for ALL non-API games (winLossPct)
//   API sports = ONE % that applies to cricket + football + tennis by default
//                (profitSharePct). A "Different per game" toggle expands to a
//                per-sport % for 🏏 Cricket / ⚽ Football / 🎾 Tennis.
// A child can never exceed its parent (Golden Limit); the backend still clamps.
// Only P&L partnership % is set here — Rebate + platform fees are global (admin-only).

import React, { useState } from 'react';

export type CommissionRates = {
  winLossPct?: number | '';        // 🎰 Casino (all non-API games)
  profitSharePct?: number | '';    // shared "same for all sports" API value
  cricketPct?: number | '';        // optional per-sport overrides
  footballPct?: number | '';
  tennisPct?: number | '';
};

type Ceilings = Partial<Record<keyof CommissionRates, number>>;

const inputCls =
  'w-full rounded-lg px-2.5 py-1.5 text-[13px] text-right tabular-nums font-mono ' +
  'bg-white dark:bg-neutral-900/50 border border-neutral-200 dark:border-white/10 ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 disabled:opacity-50';

function RateInput({
  label, sub, name, value, ceiling, onChange, disabled,
}: {
  label: string; sub?: string; name: keyof CommissionRates;
  value: number | '' | undefined; ceiling?: number;
  onChange: (patch: CommissionRates) => void; disabled?: boolean;
}) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between mb-1 gap-1">
        <span className="text-[12px] font-semibold text-neutral-700 dark:text-neutral-200 truncate">{label}</span>
        {ceiling != null && (
          <span className="shrink-0 text-[9.5px] font-bold uppercase tracking-wide px-1 py-0.5 rounded
                           bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">
            max {ceiling}%
          </span>
        )}
      </span>
      <div className="relative">
        <input
          type="number" min={0} max={ceiling ?? 100} step={0.25} disabled={disabled}
          value={value ?? ''} placeholder="0"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { onChange({ [name]: '' }); return; }
            let n = Number(raw);
            if (!Number.isFinite(n)) return;
            if (ceiling != null) n = Math.min(n, ceiling);   // Golden Limit — can't exceed parent
            onChange({ [name]: Math.max(0, n) });
          }}
          className={`${inputCls} pr-6 ${ceiling === 0 ? 'opacity-60' : ''}`}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 pointer-events-none">%</span>
      </div>
      {sub && <span className="block text-[9.5px] text-neutral-400 mt-0.5 leading-tight">{sub}</span>}
    </label>
  );
}

export default function CommissionRatesFields({
  value, onChange, ceilings, disabled,
}: {
  value: CommissionRates;
  onChange: (patch: CommissionRates) => void;
  ceilings?: Ceilings;
  disabled?: boolean;
}) {
  const c = ceilings || {};
  // Shared ("same for all") ceiling = the cricket/shared profit-share cap.
  const sportCeil = c.profitSharePct;
  // C3 (2026-08-06): each per-sport field shows its OWN parent ceiling when the caller
  // supplies one (cricketPct/footballPct/tennisPct), else falls back to the shared cap —
  // so football/tennis no longer wrongly display the cricket ceiling. `?? sportCeil` only
  // fires on null/undefined, so a real 0 ceiling stays 0. Backend still hard-clamps each
  // sport to the parent's own per-sport rate regardless.
  const ceilFor = (s: 'cricket' | 'football' | 'tennis'): number | undefined =>
    (s === 'cricket' ? c.cricketPct : s === 'football' ? c.footballPct : c.tennisPct) ?? sportCeil;
  // "Different per game" is ON automatically if any per-sport value is already set.
  const hasPerSport = [value.cricketPct, value.footballPct, value.tennisPct].some((v) => v !== '' && v != null);
  const [perGame, setPerGame] = useState<boolean>(hasPerSport);

  // When switching to per-game, seed each sport from the shared value so the broker
  // starts from what they already chose (then tweaks one).
  const openPerGame = () => {
    const s = value.profitSharePct;
    onChange({
      cricketPct: value.cricketPct ?? s ?? '',
      footballPct: value.footballPct ?? s ?? '',
      tennisPct: value.tennisPct ?? s ?? '',
    });
    setPerGame(true);
  };
  const closePerGame = () => {
    onChange({ cricketPct: '', footballPct: '', tennisPct: '' });   // clear overrides → shared value rules
    setPerGame(false);
  };

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-white/10 bg-neutral-50/60 dark:bg-white/[0.02] p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-bold text-neutral-800 dark:text-neutral-100">Partnership (P&amp;L) %</span>
        <span className="text-[10.5px] text-neutral-400">their share · always ≤ your own</span>
      </div>

      {/* 🎰 Casino — one % for every non-API game */}
      <div className="rounded-lg border border-neutral-200 dark:border-white/10 p-2.5">
        <RateInput
          label="🎰 Casino"
          sub="all games except cricket · football · tennis"
          name="winLossPct" value={value.winLossPct} ceiling={c.winLossPct}
          onChange={onChange} disabled={disabled}
        />
      </div>

      {/* API sports — shared by default, expand to per-game */}
      <div className="rounded-lg border border-neutral-200 dark:border-white/10 p-2.5 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-bold text-neutral-700 dark:text-neutral-200">🏏 ⚽ 🎾 Live sports</span>
          <button
            type="button" disabled={disabled}
            onClick={() => (perGame ? closePerGame() : openPerGame())}
            className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full border
                       border-emerald-300 text-emerald-700 dark:border-emerald-500/40 dark:text-emerald-300
                       hover:bg-emerald-500/10 disabled:opacity-50"
          >
            {perGame ? '↩ Same for all' : 'Different per game →'}
          </button>
        </div>

        {!perGame ? (
          <RateInput
            label="Your P&L share"
            sub="applies to cricket · football · tennis"
            name="profitSharePct" value={value.profitSharePct} ceiling={sportCeil}
            onChange={onChange} disabled={disabled}
          />
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <RateInput label="🏏 Cricket" sub="cricket" name="cricketPct" value={value.cricketPct} ceiling={ceilFor('cricket')} onChange={onChange} disabled={disabled} />
            <RateInput label="⚽ Football" sub="football" name="footballPct" value={value.footballPct} ceiling={ceilFor('football')} onChange={onChange} disabled={disabled} />
            <RateInput label="🎾 Tennis" sub="tennis" name="tennisPct" value={value.tennisPct} ceiling={ceilFor('tennis')} onChange={onChange} disabled={disabled} />
          </div>
        )}
      </div>

      <p className="text-[10px] text-neutral-400 leading-snug">
        Rebate &amp; platform fees are set by the admin for the whole downline — not yours to set here.
      </p>
    </div>
  );
}

// Build the API `rates` object — omit blanks. When "Same for all" is used, only the
// shared profitSharePct is sent and the backend applies it to all three sports.
export function ratesPayload(v: CommissionRates): Record<string, number> | undefined {
  const out: Record<string, number> = {};
  (Object.keys(v) as (keyof CommissionRates)[]).forEach((k) => {
    const n = v[k];
    if (n !== '' && n != null && Number.isFinite(Number(n))) out[k] = Number(n);
  });
  return Object.keys(out).length ? out : undefined;
}
